/**
 * ValuationMetricsTable — detailed multi-year valuation & comparison metrics table.
 *
 * Shows EV, Market Cap, EV/EBITDA, EV decomposition, ROIC/WACC spread, etc.
 */
import { Card, CardHeader, CardBody } from '../../ui/Card';
import DataNoteBadge, { SuppressedValue } from '../DataNoteBadge';
import { NO_MARKET_PRICE_NOTE } from '../charts/chartNotes';
import {
  formatFiscalYear,
  formatCompactCurrency,
  formatPercent,
  formatUnitLabel,
} from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function ValuationMetricsTable({ data, region }: Props) {
  const years = data;
  const unitLabel = formatUnitLabel(region);

  type RowDef = {
    label: string;
    key: keyof UnifiedFinancialRecord;
    format: 'currency' | 'pct' | 'multiple' | 'number' | 'years';
    indent?: boolean;
    bold?: boolean;
    separator?: boolean;
    sectionHeader?: string;
    /**
     * Needs a traded market price to exist. For companies without one (PDF
     * ingestions of unlisted years) the engine writes null BY DESIGN — these
     * rows stay visible with a "Not calculated — no market price" explanation
     * instead of being dropped, and must never be rendered as 0.
     */
    marketDerived?: boolean;
  };

  const rows: RowDef[] = [
    { label: 'VALUATION', key: 'enterprise_value', sectionHeader: 'Valuation Metrics', format: 'currency' },
    { label: 'Enterprise Value (EV)', key: 'enterprise_value', format: 'currency', bold: true, marketDerived: true },
    { label: 'Market Capitalisation', key: 'market_cap', format: 'currency', marketDerived: true },
    { label: 'EV / EBITDA', key: 'ev_ebitda', format: 'multiple', marketDerived: true },
    { label: '', key: 'enterprise_value', separator: true, format: 'currency' },
    { label: 'EV DECOMPOSITION', key: 'ev_from_ebit', sectionHeader: 'EV Decomposition', format: 'currency' },
    { label: 'EV from EBIT (Operations)', key: 'ev_from_ebit', format: 'currency', indent: true, marketDerived: true },
    { label: 'EV from Future Growth', key: 'ev_from_future_growth', format: 'currency', indent: true, marketDerived: true },
    { label: 'Future Growth %', key: 'future_growth_percentage', format: 'pct', indent: true, marketDerived: true },
    { label: '', key: 'roic', separator: true, format: 'pct' },
    { label: 'RETURN METRICS', key: 'roic', sectionHeader: 'Return Metrics', format: 'pct' },
    { label: 'ROIC', key: 'roic', format: 'pct', bold: true },
    { label: 'WACC', key: 'wacc', format: 'pct', marketDerived: true },
    { label: 'NOPAT', key: 'nopat', format: 'currency', indent: true },
    { label: 'Invested Capital', key: 'invested_capital', format: 'currency' },
    { label: 'D/E Ratio', key: 'debt_equity_ratio', format: 'number' },
  ];

  // True when the company has no market data at all across the shown years —
  // the PDF-without-price case. Market-derived rows then render with the
  // suppression note rather than disappearing (hiding them silently would make
  // "no market price" indistinguishable from "table forgot the metric").
  const marketDataAbsent =
    years.length > 0 &&
    years.every(
      (d) =>
        d.enterprise_value == null && d.market_cap == null && d.wacc == null && d.ev_ebitda == null
    );

  const fmt = (val: number | null | undefined, format: string) => {
    if (val == null || isNaN(val as number)) return '—';
    const v = val as number;
    if (format === 'currency') return formatCompactCurrency(v, region);
    if (format === 'pct') return formatPercent(v);
    if (format === 'multiple') return `${v.toFixed(1)}x`;
    if (format === 'years') return `${v.toFixed(1)} yrs`;
    return v.toFixed(2);
  };

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-neutral-800">
            Valuation & Return Metrics
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            EV decomposition, return metrics, and capital efficiency
          </p>
        </div>
        {unitLabel && (
          <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 bg-neutral-100 border border-neutral-200 rounded px-2 py-1 whitespace-nowrap">
            {unitLabel}
          </span>
        )}
      </CardHeader>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                <th className="text-left py-2.5 px-3 text-neutral-500 font-semibold text-[11px] uppercase tracking-wider min-w-[12rem] sticky left-0 bg-neutral-50 z-10">
                  Metric
                </th>
                {years.map((d) => (
                  <th
                    key={d.year}
                    className="text-right py-2.5 px-3 text-neutral-700 font-bold text-[11px] whitespace-nowrap min-w-[5rem]"
                  >
                    <span className="inline-flex items-center gap-1">
                      {formatFiscalYear(d.year, region)}
                      {/* Flags years whose figures were adjusted or estimated. */}
                      <DataNoteBadge notes={d.data_notes} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                if (row.separator) {
                  return (
                    <tr key={idx}>
                      <td colSpan={years.length + 1} className="py-0.5 bg-neutral-100/70" />
                    </tr>
                  );
                }
                if (row.sectionHeader) {
                  return (
                    <tr key={idx} className="bg-neutral-100/70">
                      <td
                        colSpan={years.length + 1}
                        className="py-2 px-3 text-[11px] font-bold text-neutral-600 uppercase tracking-wider border-l-2 border-primary-400 sticky left-0 z-10 bg-neutral-100"
                      >
                        {row.sectionHeader}
                      </td>
                    </tr>
                  );
                }
                const hasAnyValue = years.some((d) => d[row.key] != null);
                // Empty market-derived rows survive when the whole company is
                // unpriced — every cell then explains itself via the note below.
                if (!hasAnyValue && !(row.marketDerived && marketDataAbsent)) return null;
                const rowBg = row.bold ? 'bg-neutral-50/80' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/30';
                const labelBg = row.bold ? 'bg-neutral-50/95' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/60';
                return (
                  <tr
                    key={idx}
                    className={`border-b border-neutral-100 transition-colors hover:bg-blue-50/40 ${rowBg}`}
                  >
                    <td
                      className={`py-2 px-3 text-xs leading-snug sticky left-0 z-10 ${labelBg} ${
                        row.bold ? 'font-semibold text-neutral-800' : 'text-neutral-600'
                      } ${row.indent ? 'pl-7' : ''}`}
                    >
                      {row.label}
                    </td>
                    {years.map((d) => {
                      const val = d[row.key] as number | null | undefined;
                      const missing = val == null || isNaN(val as number);
                      const isNeg = typeof val === 'number' && val < 0 && row.format === 'currency';
                      return (
                        <td
                          key={d.year}
                          className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${
                            isNeg ? 'text-red-600' : row.bold ? 'font-semibold text-neutral-900' : 'text-neutral-700'
                          } ${row.bold ? 'font-semibold' : ''}`}
                        >
                          {/* A blank metric is explained where it is missing, not
                              just summarised in the year header. */}
                          {missing ? (
                            <SuppressedValue
                              notes={d.data_notes}
                              field={row.key as string}
                              fallbackNote={row.marketDerived ? NO_MARKET_PRICE_NOTE : undefined}
                            />
                          ) : (
                            fmt(val, row.format)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
